import { useEffect, useMemo, useState } from 'react';
import api, { assetUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '@/components/SEO';
import SocialStrip from '@/components/SocialStrip';
import ReportButton from '@/components/ReportButton';
import Pagination from '@/components/Pagination';
import { useLanguage } from '@/context/LanguageContext';

const CATEGORIES = [
  { key: 'all', tkey: 'shop.all', fallback: 'All' },
  { key: 'phone_case', tkey: 'shop.phoneCases', fallback: 'Phone Cases' },
  { key: 'sticker', tkey: 'shop.stickers', fallback: 'Stickers' },
  { key: 'label', tkey: 'shop.labels', fallback: 'Labels' },
];

const DesignsLibrary = () => {
  const [designs, setDesigns] = useState([]);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [socialMap, setSocialMap] = useState({});
  const { t } = useLanguage();
  const navigate = useNavigate();

  const load = (p = page) => {
    const params = { page: p, page_size: 24 };
    if (category !== 'all') params.category = category;
    if (search) params.search = search;
    api.get('/designs', { params }).then(({ data }) => {
      setDesigns(data.items || []);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
      setPage(data.page || p);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); load(1); }, [category]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const designIds = useMemo(() => designs.map((d) => d.id), [designs]);
  useEffect(() => {
    if (designIds.length === 0) { setSocialMap({}); return; }
    api.post('/social/batch', { items: designIds.map((id) => ({ entity_type: 'design', id })) })
      .then(({ data }) => {
        const m = {};
        for (const s of data) m[s.entity_id] = s;
        setSocialMap(m);
      })
      .catch(() => {});
  }, [designIds]);

  const openInEditor = (design) => {
    const targetProduct = design.category === 'phone_case' ? 'pc-iphone-custom' : design.category === 'sticker' ? 'st-vinyl-custom' : 'lb-custom';
    localStorage.setItem('jw_editor_prefill', JSON.stringify({ image_url: assetUrl(design.image_url), design_id: design.id, category: design.category }));
    navigate(`/design/${targetProduct}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <SEO title="Design Library — Custom Print Templates" description="Browse thousands of custom print designs for phone cases, stickers and labels. Like, save, rate and use AI-crafted templates." path="/designs" />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('designs.title', 'Design Library')}</h1>
          <p className="mt-1" style={{ color: 'var(--jw-text-muted)' }}>{t('designs.subtitle', 'Thousands of ready-to-print designs. Pick one and customize.')}</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--jw-text-subtle)' }} />
          <Input data-testid="designs-search-input" placeholder={t('designs.search', 'Search designs')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64" />
        </div>
      </div>

      <div className="mt-6 -mx-4 sm:mx-0 overflow-x-auto jw-scrollbar">
        <div className="px-4 sm:px-0 min-w-max">
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList>
              {CATEGORIES.map(c => (
                <TabsTrigger key={c.key} value={c.key} data-testid={`designs-tab-${c.key}`}>{t(c.tkey, c.fallback)}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-8">
        {designs.map(d => (
          <Card key={d.id} className="jw-card overflow-hidden group flex flex-col relative" data-testid={`design-${d.id}-card`}>
            <div className="absolute top-2 right-2 z-10">
              <ReportButton entityType="design" entityId={d.id} />
            </div>
            <Link to={`/designs/${d.id}`} data-testid={`design-${d.id}-view-link`} className="aspect-square overflow-hidden block p-2" style={{ background: 'var(--jw-bg-elev)' }}>
              <img
                src={assetUrl(d.image_url) || '/favicon.svg'}
                alt={d.name}
                onError={(e) => { e.currentTarget.src = '/favicon.svg'; }}
                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
              />
            </Link>
            <div className="p-3 flex flex-col gap-2 flex-1">
              <Link to={`/designs/${d.id}`} className="flex items-center justify-between gap-2 hover:opacity-90">
                <h3 className="font-semibold text-sm truncate">{d.name}</h3>
                {d.is_premium && <span className="text-[10px] px-2 py-0.5 rounded-full jw-gradient-bg text-white font-medium">{t('designs.premium', 'PREMIUM')}</span>}
              </Link>
              <p className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>{d.category ? t(`designCategory.${d.category}`, d.category.replace('_', ' ')) : ''}</p>
              <SocialStrip
                entityType="design"
                entityId={d.id}
                initial={socialMap[d.id]}
                onChange={(next) => setSocialMap((m) => ({ ...m, [d.id]: next }))}
              />
              <Button size="sm" onClick={() => openInEditor(d)} data-testid={`design-${d.id}-use-button`} className="jw-btn-glossy mt-auto w-full rounded-md h-9">{t('designs.useInEditor', 'Use in editor')}</Button>
            </div>
          </Card>
        ))}
        {designs.length === 0 && (
          <div className="col-span-full text-center py-16" style={{ color: 'var(--jw-text-muted)' }}>{t('designs.empty', 'No designs found. (The library is being generated by AI in the background — refresh in a moment.)')}</div>
        )}
      </div>

      <Pagination
        page={page}
        pages={pages}
        total={total}
        onChange={(p) => { setPage(p); load(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        testIdPrefix="designs-pagination"
      />
    </div>
  );
};

export default DesignsLibrary;
